import { Theme } from '@mui/material';
import moment from 'moment';

import { IViewCard } from '@/components';
import { DataGridColSettings } from '@/components/DataGrid/utils';
import { Gender, genderInput, phoneNumberInput } from '@/constants/form_inputs';
import { BloodType, IUserForm, Role } from '@/models';
import { EmployeeDetailsTabRoutes } from '@/routes/RoutePaths';

export const EmployeeTableColumns = [
  'Last name',
  'First name',
  'Middle name',
  'Email',
  'Phone number',
  'Title',
  'Rank',
  'Badge number',
  'Department',
  '',
];

export const EmployeeViewTabs = [
  { label: 'Employee Details', path: EmployeeDetailsTabRoutes.Information },
  { label: 'Device History', path: EmployeeDetailsTabRoutes.Devices },
];

export const employeeDeviceDataGridCols: DataGridColSettings[] = [
  {
    field: 'uid',
    headerName: 'Device ID',
    width: 180,
    visible: true,
  },
  {
    field: 'model',
    headerName: 'Device type',
    width: 180,
    visible: true,
  },
  {
    field: 'platform',
    headerName: 'OS',
    width: 180,
    visible: true,
  },
  {
    field: 'manufacturer',
    headerName: 'Device name',
    width: 180,
    visible: true,
  },
  {
    field: 'lastActive',
    headerName: 'Last active',
    width: 180,
    visible: true,
  },
  {
    field: 'appName',
    headerName: 'Application',
    width: 180,
    visible: true,
  },
  {
    field: 'appVersion',
    headerName: 'Application version',
    width: 180,
    visible: true,
  },
  {
    field: 'deviceStatus',
    headerName: 'Status',
    width: 180,
    visible: true,
  },
  {
    field: 'oriNumber',
    headerName: 'Device ORI',
    width: 180,
    visible: true,
  },
  {
    field: 'mnemonic',
    headerName: 'Mnemonic',
    visible: true,
  },
  {
    field: 'restrictDevice',
    headerName: 'Restrict Device',
    width: 180,
    visible: true,
  },
  {
    field: 'isNCICEnabled',
    headerName: 'NCIC Search',
    width: 180,
    visible: true,
  },
];

export const employeeDevicesCols = [
  {
    key: 'model',
    label: 'Device type',
  },
  {
    key: 'platform',
    label: 'OS',
  },
  {
    key: 'manufacturer',
    label: 'Device name',
  },
  {
    key: 'lastActive',
    label: 'Last active',
  },
  {
    key: 'appName',
    label: 'Application',
  },
  {
    key: 'appVersion',
    label: 'Application version',
  },
  {
    key: 'deviceStatus',
    label: 'Status',
  },
  {
    key: 'restrictDevice',
    label: 'Restrict Device',
  },
];

export const employeeDataGridCols: DataGridColSettings[] = [
  {
    field: 'lastName',
    headerName: 'Last Name',
    width: 170,
    visible: true,
  },
  {
    field: 'firstName',
    headerName: 'First Name',
    width: 170,
    visible: true,
  },
  {
    field: 'middleName',
    headerName: 'Middle Name',
    width: 170,
    visible: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 230,
    visible: true,
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone Number',
    width: 200,
    visible: true,
  },
  {
    field: 'profileInfo.employmentInformation.title',
    headerName: 'Title',
    width: 160,
    visible: true,
  },
  {
    field: 'profileInfo.employmentInformation.rank.name',
    headerName: 'Rank',
    width: 160,
    visible: true,
  },
  {
    field: 'profileInfo.employmentInformation.badgeNumber',
    headerName: 'Badge Number',
    width: 160,
    visible: true,
  },
  {
    field: 'profileInfo.employmentInformation.department.name',
    headerName: 'Department',
    width: 170,
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    visible: true,
  },
];

export const EmployeeFormFields = [
  {
    title: 'Employee information',
    items: [
      {
        name: 'lastName',
        type: 'text',
        label: 'Last Name',
        rules: {
          required: 'This field is required.',
          validate: {
            minLength: (value: string) =>
              !value ||
              value.trim().length > 3 ||
              'Name should be more than 3 characters.',
            maxLength: (value: string) =>
              !value ||
              value.trim().length <= 200 ||
              'Name should be less than 200 characters.',
          },
          pattern: {
            value: /^[A-Za-z\s.'-]*$/,
            message: 'Invalid name',
          },
        },
      },
      {
        name: 'firstName',
        type: 'text',
        label: 'First Name',
        rules: {
          required: 'This field is required.',
          validate: {
            minLength: (value: string) =>
              !value ||
              value.trim().length > 2 ||
              'Name should be more than 2 characters.',
            maxLength: (value: string) =>
              !value ||
              value.trim().length <= 200 ||
              'Name should be less than 200 characters.',
          },
          pattern: {
            value: /^[A-Za-z\s.'-]*$/,
            message: 'Invalid name',
          },
        },
      },
      {
        name: 'middleName',
        type: 'text',
        label: 'Middle Name',
        rules: {
          validate: {
            maxLength: (value: string) =>
              !value ||
              value.trim().length <= 50 ||
              'Middle Name should be less than 50 characters.',
          },
          pattern: {
            value: /^[A-Za-z\s.'-]*$/,
            message: 'Invalid name',
          },
        },
      },
      {
        ...genderInput,
        name: 'profile.gender',
      },
      {
        name: 'profile.birthday',
        type: 'date',
        label: 'Date of birth',
        minDate: moment('1900-01-01'),
        maxDate: moment(),
        disableFuture: true,
        rules: {
          validate: {
            notInFuture: (value: string | moment.Moment | null) =>
              !value ||
              !moment(value).isAfter(moment().startOf('day')) ||
              'Date of birth cannot be in the future',
          },
        },
      },
      {
        name: 'profile.bloodType',
        type: 'autocomplete',
        label: 'Blood type',
        items: [
          {
            label: 'A+',
            value: BloodType.A_POSITIVE,
          },
          {
            label: 'A-',
            value: BloodType.A_NEGATIVE,
          },
          {
            label: 'B+',
            value: BloodType.B_POSITIVE,
          },
          {
            label: 'B-',
            value: BloodType.B_NEGATIVE,
          },
          {
            label: 'O+',
            value: BloodType.O_POSITIVE,
          },
          {
            label: 'O-',
            value: BloodType.O_NEGATIVE,
          },
          {
            label: 'AB+',
            value: BloodType.AB_POSITIVE,
          },
          {
            label: 'AB-',
            value: BloodType.AB_NEGATIVE,
          },
          {
            label: 'Unknown',
            value: BloodType.UNKNOWN,
          },
        ],
        rules: {
          required: false,
        },
      },
      {
        ...phoneNumberInput,
        rules: {
          ...phoneNumberInput.rules,
        },
      },
      {
        name: 'email',
        type: 'email',
        label: 'Email address',
        rules: {
          required: 'This field is required.',
          pattern: {
            value:
              /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/, // eslint-disable-line
            message: 'Invalid email',
          },
        },
      },
    ],
  },
  {
    title: 'Address information',
    name: 'profile',
    items: [
      {
        name: 'address',
        type: 'address',
        label: 'Address',
        grid: {
          md: 6,
        },
      },
    ],
  },
  {
    title: 'Emergency contact',
    name: 'profile.emergencyContact',
    items: [
      {
        name: 'name',
        type: 'text',
        label: 'Emergency contact name',
        rules: {
          validate: {
            minLength: (value: string) =>
              !value ||
              value.trim().length > 3 ||
              'Name should be more than 3 characters.',
            maxLength: (value: string) =>
              !value ||
              value.trim().length <= 200 ||
              'Name should be less than 200 characters.',
          },
          pattern: {
            value: /^[A-Za-z\s]*$/,
            message: 'Invalid name',
          },
        },
      },
      {
        ...phoneNumberInput,
        name: 'phoneNumber',
        label: 'Emergency contact phone',
        rules: {
          ...phoneNumberInput.rules,
        },
      },
    ],
  },
  {
    title: 'Employment information',
    name: 'profile.employmentInformation',
    items: [
      {
        name: 'title',
        type: 'text',
        label: 'Title',
        grid: {
          sm: 12,
          md: 3,
        },
      },
      {
        name: 'supervisor',
        type: 'autocomplete',
        label: 'Supervisor',
        clearable: true,
        grid: {
          sm: 12,
          md: 3,
          sx: (theme: Theme) => ({
            [theme.breakpoints.up('md')]: {
              marginRight: '50%',
            },
          }),
        },
      },
      {
        name: 'rank',
        type: 'autocomplete',
        label: 'Rank',
      },
      {
        name: 'badgeNumber',
        type: 'text',
        label: 'Badge number',
      },
      {
        name: 'department',
        type: 'autocomplete',
        label: 'Department',
      },
      {
        name: 'seniority',
        type: 'number',
        label: 'Seniority',
        rules: {
          required: false,
          min: {
            value: 0,
            message: 'Seniority must be at least 0.',
          },
          pattern: {
            value: /^[0-9]+$/,
            message: 'The value must be a whole number',
          },
        },
      },
      {
        name: 'stateId',
        type: 'text',
        label: 'State ID',
        grid: {
          sm: 12,
          md: 3,
        },
      },
    ],
  },
  {
    title: 'Additional Information',
    items: [
      {
        name: 'gpsTracker',
        type: 'select',
        label: 'GPS Tracking Equipment',
      },
    ],
  },
];

export const EmployeeViewFields: IViewCard[] = [
  {
    title: 'Employee information',
    key: 'employeeInfo',
    size: { md: 12 },

    items: [
      {
        value: 'lastName',
        label: 'Last Name',
      },
      {
        value: 'firstName',
        label: 'First Name',
      },
      {
        value: 'middleName',
        label: 'Middle Name',
      },
      {
        label: 'Gender',
        value: 'profile.gender',
      },
      {
        value: 'profile.birthday',
        label: 'Date of birth',
      },
      {
        value: 'profile.bloodType',
        label: 'Blood type',
      },
      {
        value: 'phoneNumber',
        label: 'Phone number',
      },
      {
        value: 'status',
        label: 'Status',
      },
      {
        value: 'email',
        label: 'Email address',
      },
      {
        value: 'lastLoggedIn',
        label: 'Last Login',
      },
    ],
  },
  {
    title: 'Address information',
    name: 'profile',
    key: 'profile.address',
    size: { md: 6 },
    items: [
      {
        value: 'address',
        label: 'Address ',
      },
    ],
  },
  {
    title: 'Emergency contact',
    name: 'profile.emergencyContact',
    key: 'emergencyContact',
    size: { md: 6 },
    items: [
      {
        value: 'name',
        label: 'Emergency contact name',
      },
      {
        value: 'phoneNumber',
        label: 'Emergency contact phone',
      },
    ],
  },
  {
    title: 'Employment information',
    name: 'profile.employmentInformation',
    key: 'employmentInfo',
    size: { md: 12 },
    items: [
      {
        value: 'title',
        label: 'Title',
      },
      {
        value: 'supervisor.fullName',
        label: 'Supervisor',
      },
      {
        value: 'rank.name',
        label: 'Rank',
      },
      {
        value: 'badgeNumber',
        label: 'Badge number',
      },
      {
        value: 'department.name',
        label: 'Department',
      },
      {
        value: 'seniority',
        label: 'Seniority',
      },
      {
        value: 'stateId',
        label: 'State ID',
      },
    ],
  },
  {
    title: 'Additional Information',
    key: 'additional_info',
    size: { md: 12 },
    items: [
      {
        value: 'gpsTracker.name',
        label: 'GPS Tracking Equipment',
      },
    ],
  },
  {
    title: 'Certification',
    name: 'profile',
    key: 'certification',
    size: { md: 12 },
    items: [
      {
        value: 'certifications',
        label: 'Certification',
      },
    ],
  },
];

export const CertificateFields = (index: number, watch: any) => {
  const certRequired = watch(`profile.certifications.${index}.required`);

  return [
    {
      name: 'name',
      type: 'text',
      label: 'Certification',
      rules: {
        required: 'This field is required.',
      },
      grid: {
        md: 4,
      },
    },
    {
      name: 'required',
      type: 'select',
      label: 'Certification Required?',
      items: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
      rules: {
        required: 'This field is required.',
      },
      grid: {
        md: 2,
      },
    },
    {
      name: 'expireDate',
      type: 'date',
      label: 'Expiration date',
      minDate: moment().add(1, 'days'),
      rules: {
        required:
          certRequired === 'Yes' ? 'Expiration date is required.' : false,
      },
      grid: {
        md: 2,
      },
    },
    {
      name: 'file.name',
      type: 'text',
      label: 'Certificate file',
      readOnly: true,
      rules: {
        required: 'File is required.',
      },
      grid: {
        md: 2,
      },
    },
  ];
};

export const EmployeeDefaultValues: IUserForm = {
  email: '',
  phoneNumber: '',
  firstName: '',
  middleName: '',
  lastName: '',
  stateId: '',
  profile: {
    gender: Gender.UNKNOWN,
    birthday: '',
    bloodType: BloodType.UNKNOWN,
    emergencyContact: {
      name: '',
      phoneNumber: '',
    },
    certifications: [],
    employmentInformation: {
      title: '',
      badgeNumber: '',
      seniority: 0,
      stateId: '',
    },
  },
  roles: [],
};

export const AgencyRoleItems = [
  {
    label: 'Employee',
    value: Role.EMPLOYEE,
    isDisabled: true,
    tooltip: 'This role is automatically assigned to all employees.',
  },
  {
    label: 'Agency admin',
    value: Role.AGENCY_ADMIN,
  },
  {
    label: 'First responder',
    value: Role.FIRST_RESPONDER,
  },
  // RMS Cases (replaces the legacy Police role) and KASE_APPROVER exist only for
  // POLICE type agency. The two RMS Cases roles render as a grouped "RMS cases"
  // section in the modal, so they are flagged as sub-roles here.
  {
    label: 'RMS cases - Full access',
    value: Role.RMS_CASES_FULL_ACCESS,
    isSubRole: true,
  },
  {
    label: 'RMS cases - View',
    value: Role.RMS_CASES_VIEW,
    isSubRole: true,
  },
  {
    label: 'Case approver',
    value: Role.KASE_APPROVER,
  },
  {
    label: 'Evidence collector',
    value: Role.EVIDENCE_COLLECTOR,
  },
  {
    label: 'Evidence custodian',
    value: Role.EVIDENCE_CUSTODIAN,
  },
  {
    label: 'Evidence transporter',
    value: Role.EVIDENCE_TRANSPORTER,
  },
  // Fire agency roles
  {
    label: 'Fire RMS',
    value: Role.FIRE_RMS,
  },
  {
    label: 'Jail Officer',
    value: Role.JAIL_OFFICER,
  },
  {
    label: 'NCIC Authorized',
    value: Role.NCIC_AUTHORIZED,
    isDisabled: false,
    tooltip: 'This role is only available for Users without StateID.',
  },
  {
    label: 'Access to Office chat',
    value: Role.ACCESS_OFFICE_CHAT,
  },
  {
    label: 'Office chat Admin',
    value: Role.OFFICE_CHAT_ADMIN,
  },
  {
    label: 'NIBRS Report Exporter',
    value: Role.NIBRS_REPORT_EXPORTER,
  },
  {
    label: 'Skydio Notifications',
    value: Role.SKYDIO_NOTIFICATIONS,
  },
];

export const AccountRoleItems = [
  {
    label: 'Account admin',
    value: Role.ACCOUNT_ADMIN,
  },
  {
    label: 'Viewer',
    value: Role.VIEWER,
  },
  {
    label: 'Dispatcher',
    value: Role.DISPATCHER,
  },
  {
    label: 'CFS (View Only)',
    value: Role.CFS_VIEW_ONLY,
  },
  {
    label: 'Access Tasks',
    value: Role.ACCESS_TASKS,
  },
  {
    label: 'Call log access',
    value: Role.CALL_LOG_ACCESS,
    isSubRole: true,
  },
  {
    label: 'Call recording access',
    value: Role.CALL_RECORDING_ACCESS,
    isSubRole: true,
  },
  {
    label: 'Tools manager',
    value: Role.TOOLS_MANAGER,
  },
  {
    label: 'Wanted Person - Create/Edit',
    value: Role.WANTED_PERSON_EDIT,
    isSubRole: true,
  },
  {
    label: 'Wanted Person - View',
    value: Role.WANTED_PERSON_VIEW,
    isSubRole: true,
  },
  {
    label: 'Wanted Person - View/Add Attempt',
    value: Role.WANTED_PERSON_VIEW_ADD_ATTEMPT,
    isSubRole: true,
  },
  {
    label: 'Calendar & Schedules - Create/Edit/Delete Own Events',
    value: Role.CALENDAR_SCHEDULES_CREATE_EDIT_DELETE_OWN,
    isSubRole: true,
  },
  {
    label: "Calendar & Schedules - Edit/Delete Other Users' Events",
    value: Role.CALENDAR_SCHEDULES_EDIT_DELETE_OTHERS,
    isSubRole: true,
  },
  {
    label: 'Calendar & Schedules - Approve/Decline Event Requests',
    value: Role.CALENDAR_SCHEDULES_APPROVE_REQUESTS,
    isSubRole: true,
  },
  {
    label: 'IA Officer',
    value: Role.IA_OFFICER,
    isSubRole: true,
  },
  {
    label: 'Can manage sex offenders',
    value: Role.SEX_OFFENDER_MANAGE,
    isSubRole: true,
  },
];

export const DeviceNcicOriFormFields = [
  {
    name: 'oriNumber',
    label: 'Device ORI (Optional)',
    rules: {
      maxLength: {
        value: 50,
        message: 'ORI should be less than 50 characters.',
      },
      validate: (value: string, formValues: any) => {
        if (!value && !formValues.mnemonic) {
          return '';
        }
        return true;
      },
    },
    grid: {
      md: 12,
    },
  },
  {
    name: 'mnemonic',
    label: 'Device Mnemonic (Optional)',
    rules: {
      maxLength: {
        value: 50,
        message: 'Mnemonic should be less than 50 characters.',
      },
      validate: (value: string, formValues: any) => {
        if (!value && !formValues.oriNumber) {
          return '';
        }
        return true;
      },
    },
    grid: {
      md: 12,
    },
  },
];

export interface DeviceNcicOriForm {
  oriNumber?: string;
  mnemonic?: string;
}

export const DeviceOriFormDefaultValues = {
  oriNumber: '',
  mnemonic: '',
};

export const EmployeeStatusChangeFormFields = [
  {
    name: 'reason',
    label: 'Reason',
    type: 'text-area',
    placeholder: 'Please provide a reason...',
    rules: {
      required: 'This field is required.',
      maxLength: {
        value: 200,
        message: 'Reason should be less than 200 characters.',
      },
    },
    grid: {
      sm: 12,
    },
  },
];

export interface EmployeeStatusChangeForm {
  reason: string;
}

export const EmployeeStatusChangeFormDefaultValues = {
  reason: '',
};
